import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { NAV, SITE } from "@/data/site";

const Logo = () => (
  <Link to="/" data-testid="header-logo" className="flex items-center group">
    <img
      src={`${process.env.PUBLIC_URL}/edgeworks-logo.png`}
      alt="Edgeworks Manufacturing"
      className="h-10 w-auto sm:h-11"
    />
  </Link>
);

const QuoteButton = ({ className = "", testid }) => (
  <Link
    to="/request-a-quote"
    data-testid={testid}
    className={`inline-flex items-center gap-2 bg-orange-500 px-5 py-2.5 font-semibold text-white transition-colors hover:bg-orange-600 ${className}`}
  >
    Request a Quote <ArrowRight size={16} />
  </Link>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 border-b border-neutral-800 bg-neutral-950/90 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3.5 sm:px-6 lg:px-8">
        <Logo />
        <nav className="hidden items-center gap-7 lg:flex">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${isActive ? "text-orange-500" : "text-neutral-300 hover:text-white"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:block">
          <QuoteButton testid="header-quote-btn" />
        </div>
        <button
          data-testid="mobile-menu-toggle"
          className="text-white lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>
      {open && (
        <div className="border-t border-neutral-800 bg-neutral-950 lg:hidden" data-testid="mobile-menu">
          <nav className="flex flex-col px-4 py-3">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `py-3 text-base font-medium ${isActive ? "text-orange-500" : "text-neutral-200"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <QuoteButton testid="mobile-quote-btn" className="mt-3 justify-center" />
          </nav>
        </div>
      )}
    </header>
  );
};

export const QuoteBand = () => (
  <section className="border-y border-neutral-800 bg-neutral-900" data-testid="quote-band">
    <div className="mx-auto flex max-w-7xl flex-col items-start gap-6 px-4 py-14 sm:px-6 md:flex-row md:items-center md:justify-between lg:px-8">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-orange-500">Get started</p>
        <h2 className="mt-2 font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">
          Have a drawing, model, or sample part?
        </h2>
        <p className="mt-2 max-w-xl text-neutral-400">
          Send it over and I'll take a look. Include material, quantity, and required date for the fastest quote.
        </p>
      </div>
      <QuoteButton testid="band-quote-btn" className="shrink-0 px-6 py-3 text-base" />
    </div>
  </section>
);

export const Footer = () => (
  <footer className="border-t border-neutral-800 bg-neutral-950" data-testid="site-footer">
    <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 md:grid-cols-3 lg:px-8">
      <div>
        <Logo />
        <p className="mt-4 max-w-xs text-sm text-neutral-400">
          Owner-operated CNC machining for prototypes, repairs, and small production runs.
        </p>
      </div>
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-neutral-500">Navigate</p>
        <ul className="mt-4 space-y-2">
          {NAV.map((n) => (
            <li key={n.to}>
              <Link to={n.to} className="text-sm text-neutral-300 hover:text-orange-500">{n.label}</Link>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-neutral-500">Contact</p>
        <ul className="mt-4 space-y-2 text-sm text-neutral-300">
          <li><a href={`mailto:${SITE.email}`} className="hover:text-orange-500">{SITE.email}</a></li>
          <li className="text-neutral-400">{SITE.area}</li>
        </ul>
      </div>
    </div>
    <div className="border-t border-neutral-800 px-4 py-6 text-center text-xs text-neutral-500 sm:px-6 lg:px-8">
      © {new Date().getFullYear()} Edgeworks Manufacturing · Eastern Ontario CNC Machining
    </div>
  </footer>
);

export default function Layout({ children }) {
  const loc = useLocation();
  const hideQuoteBand = loc.pathname === "/request-a-quote";
  return (
    <div className="flex min-h-screen flex-col bg-neutral-950">
      <Header />
      <main className="flex-1" key={loc.pathname}>{children}</main>
      {!hideQuoteBand && <QuoteBand />}
      <Footer />
    </div>
  );
}
